import { isPermitted } from 'src/js/userdata.js';
import { userFields, meRequestFields } from 'src/js/constants';
import { datadogRum } from 'src/js/datadog.js';
import { readItems, readMe, updateMe } from '@directus/sdk';

export function getDirectusInstance() {
  if (typeof window !== 'undefined' && window.$crm) {
    return window.$crm;
  }

  const { createDirectus, rest, staticToken, authentication } = require('@directus/sdk');
  const authToken = localStorage.getItem('crm_auth_token');
  let client = createDirectus(process.env.CRM_URL)
    .with(rest())
    .with(authentication('json'));
  
  if (authToken) {
    client = client.with(staticToken(authToken));
  }

  return client;
}

let authCache = {
  data: null,
  token: null,
  timestamp: null,
  ttl: 12 * 60 * 1000
};

export function isToken() {
  const auth_token = localStorage.getItem('crm_auth_token');
  return auth_token != null && typeof auth_token != 'undefined';
}

function validUserObject(user) {
  try {
    const userKeys = Object.keys(user);

    for (let i = 0; i <= userKeys.length - 1; i++) {
      if (!userFields.includes(userKeys[i])) {
        return false;
      }
    }
  } catch (e) {
    console.log(e);
    return false;
  }
  return true;
}

export async function doesPermittedUserExist(user) {
  try {
    if (isToken()) {
      const directus = getDirectusInstance();
      try {
        const currentUser = await directus.request(readMe());
        if (validUserObject(currentUser)) {
          if (currentUser.status === 'active' && isPermitted(currentUser.role)) {
            return true;
          }
        }
      } catch (e) {
        return false;
      }
    }
  } catch (e) {
    console.log(e);
    return false;
  }
  return false;
}

export async function authByToken() {
  const auth_token = localStorage.getItem('crm_auth_token');
  if (auth_token === null || typeof auth_token === 'undefined') {
    clearAuthCache();
    return false;
  } else if (Object.prototype.toString.call(auth_token) === '[object String]') {
    if (
      authCache.data &&
      authCache.token === auth_token &&
      authCache.timestamp &&
      Date.now() - authCache.timestamp < authCache.ttl
    ) {
      return authCache.data;
    }    

    try {
        const directus = getDirectusInstance();
        const privateData = await directus.request(readMe({
          fields: meRequestFields
        }));

        if (validUserObject(privateData)) {

          privateData.role = privateData.role?.name ?? '';

          // Kept before the pharmacy-wide values below overwrite them: per-user
          // settings (the realtime toggle) live on the employee's own record.
          privateData.own_allowed_operations = Array.isArray(privateData.allowed_operations)
            ? privateData.allowed_operations
            : [];

          const mainPharmacyUser = privateData.main_pharmacy;
          privateData.main_pharmacy = mainPharmacyUser?.id ?? null;

          if (mainPharmacyUser) {

            privateData.main_pharmacy_token = mainPharmacyUser.pharmacy_token;
            privateData.show_import_csv_tab = mainPharmacyUser.show_import_csv_tab;
            privateData.enable_doctors_csv_report = mainPharmacyUser.enable_doctors_csv_report;
            privateData.allowed_delivery_sources = mainPharmacyUser?.allowed_delivery_sources || [];
            // Empty / missing = all providers visible (default).
            privateData.hidden_providers = Array.isArray(mainPharmacyUser?.hidden_providers)
              ? mainPharmacyUser.hidden_providers
              : [];
            privateData.delivery_request_timeout = mainPharmacyUser?.delivery_request_timeout;
            privateData.enable_third_product_category = mainPharmacyUser.enable_third_product_category === true;
            privateData.allowed_operations = mainPharmacyUser.allowed_operations || [];
            privateData.allowed_bulk_operations = mainPharmacyUser.allowed_bulk_operations;
            // VAT rate of the pharmacy as a percent, e.g. "19".
            privateData.mwst_percent = mainPharmacyUser.mwst_percent;
          }

          // Both belong to the pharmacy, not to the employee who is logged in.
          const cansativa = await getPharmacyCansativaAccess(
            privateData.main_pharmacy_token || privateData.pharmacy_token
          );
          privateData.pharmacy_btm_number = cansativa.btmNumber;
          privateData.pharmacy_cansativa_api_key = cansativa.apiKey;

          datadogRum.setGlobalContextProperty('main_pharmacy_id', privateData?.main_pharmacy)

          if (privateData.status === 'active' && isPermitted(privateData.role)) {
            const result = { ...privateData };
            authCache.data = result;
            authCache.token = auth_token;
            authCache.timestamp = Date.now();
            return result;
        }
      }
    } catch (exception) {
      console.log('Something went wrong while logging in: ', exception);
      clearAuthCache();
      return false;
    }
  }

  clearAuthCache();
  return false;
}

export function clearAuthCache() {
  authCache.data = null;
  authCache.token = null;
  authCache.timestamp = null;
}

export async function refreshAuthCache() {
  clearAuthCache();
  return await authByToken();
}

export async function readCrmPharmacyUser() {
  const auth_token = localStorage.getItem('crm_auth_token');

  if (auth_token === null || typeof auth_token === 'undefined') {
    return false;
  } else {
   try {
     const directus = getDirectusInstance();
     const privateData = await directus.request(readMe());
     return privateData;
   } catch (e) {
       console.log('Something went wrong while logging in: ', e);
       return false;
   }
  }
}

export async function updatePackagingTypeCrmPharmacy(data) {
  const auth_token = localStorage.getItem('crm_auth_token');

  if (auth_token === null || typeof auth_token === 'undefined') {
    return false;
  } else {
    try {
      const directus = getDirectusInstance();
      const privateData = await directus.request(updateMe(data));
      return privateData;
    } catch (e) {
      console.log('Something went wrong while logging in: ', e);
      return false;
    }
  }
}

export async function updatePharmacyScalesCrmPharmacy(scales) {
  const auth_token = localStorage.getItem('crm_auth_token');

  if (auth_token === null || typeof auth_token === 'undefined') {
    return false;
    } else {
    try {
      const directus = getDirectusInstance();
      const privateData = await directus.request(updateMe(scales));
      return privateData;
    } catch (e) {
      console.log('Something went wrong while logging in: ', e);
      return false;
    }
  }
}

export async function updateManufacturingInstrCrmPharmacyUser(items) {
  const auth_token = localStorage.getItem('crm_auth_token');

  if (auth_token === null || typeof auth_token === 'undefined') {
    return false;
  } else {
    try {
      const directus = getDirectusInstance();
      const privateData = await directus.request(updateMe(items));
      return privateData;
    } catch (e) {
      console.log('Something went wrong while logging in: ', e);
      return false;
    }
  }
}

export async function getToken(key, token) {
  if (key) {
    const directus = getDirectusInstance();
    const query = {
      filter: {
        key: {
          _eq: key
        }
      },
      limit: 1
    };

    let result = await directus.request(readItems('tokens', query));

    return result[0]?.value;
  }
}

// Reads the pharmacy's row in the tokens table. The BtM number and the Cansativa
// key together decide whether the Cansativa columns and the cart are shown.
const NO_CANSATIVA_ACCESS = { btmNumber: null, apiKey: null };

export async function getPharmacyCansativaAccess(pharmacyToken) {
  if (!pharmacyToken) return NO_CANSATIVA_ACCESS;

  try {
    const directus = getDirectusInstance();
    const rows = await directus.request(readItems('tokens', {
      filter: { key: { _eq: pharmacyToken } },
      fields: ['pharmacy_btm_number', 'pharmacy_cansativa_api_key'],
      limit: 1
    }));

    const row = rows?.[0] || {};
    const btmNumber = String(row.pharmacy_btm_number || '').trim();
    const apiKey = String(row.pharmacy_cansativa_api_key || '').trim();

    return { btmNumber: btmNumber || null, apiKey: apiKey || null };
  } catch (error) {
    // A missing row only hides the Cansativa UI, it never blocks the login.
    console.error('Could not read the Cansativa access data:', error);
    return NO_CANSATIVA_ACCESS;
  }
}

export async function getCredentials(key) {
  if (key) {
    const directus = getDirectusInstance();
    const query = {
      filter: {
        key: {
          _eq: key
        }
      },
      fields: ["cardflo_merchant_id", "value"],
      limit: 1
    };

    const result = await directus.request(readItems('tokens', query));

    if (!result && !Array.isArray(result) && result.length !== 1) {
      throw new Error('token not found');
    }

    return {
      cardflo_merchant_id: result[0].cardflo_merchant_id,
      value: result[0].value
    };
  }
}

export async function getPharmacyOwner(key) {
  if (key) {
    const directus = getDirectusInstance();
    const query = {
      filter: {
        key: {
          _eq: key
        }
      },
      fields: ['pharmacy_owner'],
      limit: 1
    };

    const result = await directus.request(readItems('tokens', query));

    if (!result || !Array.isArray(result) || result.length !== 1) {
      return null;
    }

    return result[0]?.pharmacy_owner;
  }
  return null;
}

export async function getMainPharmacyUser(mainPharmacyId, fields = [], authToken = null) {
  if (!mainPharmacyId) {
    return null;
  }

  const auth_token = authToken || localStorage.getItem('crm_auth_token');
  if (!auth_token) {
    return null;
  }

  const crmUrl = process.env.CRM_URL;
  const fieldsParam = Array.isArray(fields) ? fields.join(',') : fields;
  
  try {
    const response = await fetch(`${crmUrl}users/${mainPharmacyId}?fields=${fieldsParam}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${auth_token}`,
        'Content-Type': 'application/json'
      }
    });
    
    const data = await response.json();
    return data.data || data;
  } catch (error) {
    console.error('Error fetching main pharmacy user:', error);
    return null;
  }
}